import dayjs from 'dayjs';
import { z } from 'zod';
import { enumLabels } from '../common/fields';
import { VIDEO_PLAYBACK_PROVIDER_LABELS, VideoPlaybackProvider } from './enums';
import {
  SearchStringSchema,
  SubjectLabelSchema,
  SubjectSchema,
  UnixTimestampSchema,
  UUIDSchema,
} from '../common/schemas';
import {
  DBVideoCategoryCategorySchema,
  DBVideoCategoryShortLabelSchema,
  DBVideoCategoryTopicResourcesSchema,
  DBVideoCategoryTopicShortLabelSchema,
  DBVideoCategoryTopicVideosSchema,
  VideoCategoryCodeSchema,
} from '../videoCategories/schemas';

/**
 * Shared & Helper Schemas
 */
export const DBVideoVideoIdSchema = UUIDSchema;
export const DBVideoTitleSchema = z.string().max(128);
export const DBVideoVideosEntrySchema = z.object({
  subject: SubjectSchema,
  title: DBVideoTitleSchema,
  videoId: DBVideoVideoIdSchema,
});
export const DBVideoRelatedVideoSchema = DBVideoVideosEntrySchema;
export const DBVideoResourceSchema = z.object({
  label: z.string().min(1).max(128),
  // Not using HttpUrlSchema, because resource hrefs can be internal links as well
  href: z.string().min(1).max(2048),
});
// Matches DBLessonTemplateLessonIdSchema: a video topic is keyed by the lesson it
// belongs to, and a lesson code is not always four characters (e.g. F1L10)
export const DBVideoTopicKeySchema = z.string().regex(/^[a-zA-Z0-9]{1,8}$/);

/**
 * DB Schemas
 */

export const DBVideoSubjectSchema = SubjectSchema;
export const DBVideoCreationDatetimeSchema = UnixTimestampSchema;
export const DBVideoDescriptionSchema = z.string().max(4096);
export const DBVideoHostSchema = z.string().max(64);
export const DBVideoPathLabelsSchema = z.lazy(() =>
  z.tuple([
    SubjectLabelSchema,
    DBVideoCategoryShortLabelSchema,
    DBVideoCategoryTopicShortLabelSchema,
  ])
);
export const DBVideoRelatedVideosSchema = z
  .array(DBVideoRelatedVideoSchema)
  .refine(relatedVideos => {
    // Check that related videos are unique
    const videoIds = relatedVideos.map(rv => rv.videoId);
    return videoIds.length === new Set(videoIds).size;
  }, 'Related videos must be unique');
export const DBVideoResourcesSchema = z.array(DBVideoResourceSchema).refine(resources => {
  // Check that resources are unique
  const labels = resources.map(r => r.label);
  return labels.length === new Set(labels).size;
}, 'Resource labels must be unique');
export const DBVideoSearchStrSchema = SearchStringSchema;
export const DBVideoTopicSchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 3 &&
    SubjectSchema.safeParse(parts[0]).success &&
    z.lazy(() => DBVideoCategoryCategorySchema).safeParse(parts[1]).success &&
    DBVideoTopicKeySchema.safeParse(parts[2]).success
  );
});
export const DBVideoTopicResourcesSchema = z.lazy(() => DBVideoCategoryTopicResourcesSchema);
export const DBVideoTopicVideosSchema = z.lazy(() => DBVideoCategoryTopicVideosSchema);
export const DBVideoYoutubeIdSchema = z
  .string()
  // Given a YouTube URL or video ID, it returns only the video ID
  .transform(url => {
    // Regular expression pattern to match YouTube URLs
    const pattern =
      /(?:(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/watch\?v=))(.*?)(?:$|\s)/i;

    // Extract the YouTube ID using the pattern
    const match = url.match(pattern);

    // If a match is found, return the YouTube ID, otherwise return the input text
    if (match && match[1]) {
      return match[1];
    }
    return url;
  })
  .pipe(
    z
      .string()
      .length(11)
      .regex(/[a-zA-Z0-9-,_]/)
  );

/**
 * Gumlet asset IDs are 24-character hex in every account seen so far, but the
 * bound that matters is the character class: the ID is interpolated into the
 * embed path that the playback signature covers, so anything outside
 * `[a-zA-Z0-9]` could reshape that path.
 */
export const DBVideoGumletAssetIdSchema = z.string().regex(/^[a-zA-Z0-9]{16,48}$/);
export const DBVideoPlaybackProviderSchema = z
  .enum(VideoPlaybackProvider)
  .register(enumLabels, { labels: VIDEO_PLAYBACK_PROVIDER_LABELS });

export const DBVideoSchema = z.object({
  subject: DBVideoSubjectSchema,
  videoId: DBVideoVideoIdSchema,
  creationDatetime: DBVideoCreationDatetimeSchema,
  description: DBVideoDescriptionSchema,
  host: DBVideoHostSchema,
  pathLabels: DBVideoPathLabelsSchema,
  relatedVideos: DBVideoRelatedVideosSchema,
  resources: DBVideoResourcesSchema,
  searchStr: DBVideoSearchStrSchema,
  title: DBVideoTitleSchema,
  topic: DBVideoTopicSchema,
  topicResources: DBVideoTopicResourcesSchema,
  topicVideos: DBVideoTopicVideosSchema,
  // Optional on both sides: a Gumlet record carries no YouTube ID, and a YouTube
  // record may carry no provider. `resolveVideoSource` decides which
  // combinations are a complete source.
  youtubeId: DBVideoYoutubeIdSchema.optional(),
  playbackProvider: DBVideoPlaybackProviderSchema.optional(),
  gumletAssetId: DBVideoGumletAssetIdSchema.optional(),
});

/**
 * Client Schemas
 */

export const VideoSubjectSchema = DBVideoSubjectSchema;
export const VideoVideoIdSchema = DBVideoVideoIdSchema;
export const VideoCreationDatetimeSchema = DBVideoCreationDatetimeSchema;
export const VideoDescriptionSchema = DBVideoDescriptionSchema;
export const VideoHostNameSchema = DBVideoHostSchema;
export const VideoPathLabelsSchema = DBVideoPathLabelsSchema;
export const VideoRelatedVideosSchema = DBVideoRelatedVideosSchema;
export const VideoResourcesSchema = DBVideoResourcesSchema;
export const VideoSearchStrSchema = DBVideoSearchStrSchema;
export const VideoTitleSchema = DBVideoTitleSchema;
export const VideoTopicCodeSchema = DBVideoTopicKeySchema;
export const VideoTopicIdSchema = z.string().brand<'VideoTopicId'>();
export const VideoTopicResourcesSchema = DBVideoTopicResourcesSchema;
export const VideoTopicVideosSchema = DBVideoTopicVideosSchema;
export const VideoYoutubeIdSchema = DBVideoYoutubeIdSchema;
export const VideoPlaybackProviderSchema = DBVideoPlaybackProviderSchema;
export const VideoGumletAssetIdSchema = DBVideoGumletAssetIdSchema;

export const VideoSchema = z.object({
  subject: VideoSubjectSchema.meta({ title: 'Subject' }),
  id: VideoVideoIdSchema.meta({ title: 'Video ID' }),
  creationDatetime: VideoCreationDatetimeSchema.default(() => dayjs().valueOf()).meta({
    title: 'Created at',
  }),
  description: VideoDescriptionSchema.default('').meta({ title: 'Description' }),
  hostName: VideoHostNameSchema.default('Cognito Tuition').meta({ title: 'Host' }),
  pathLabels: VideoPathLabelsSchema.meta({ title: 'Path labels' }),
  relatedVideos: VideoRelatedVideosSchema.default([]).meta({ title: 'Related videos' }),
  resources: VideoResourcesSchema.default([]).meta({ title: 'Resources' }),
  searchStr: VideoSearchStrSchema.default(''),
  title: VideoTitleSchema.meta({ title: 'Title' }),
  categoryCode: z.lazy(() => VideoCategoryCodeSchema).meta({ title: 'Category' }),
  topicCode: VideoTopicCodeSchema.meta({ title: 'Topic' }),
  topicId: VideoTopicIdSchema.meta({ title: 'Topic ID' }),
  topicResources: VideoTopicResourcesSchema.default([]).meta({ title: 'Topic resources' }),
  topicVideos: VideoTopicVideosSchema.default([]).meta({ title: 'Topic videos' }),
  youtubeId: VideoYoutubeIdSchema.optional().meta({ title: 'YouTube ID' }),
  playbackProvider: VideoPlaybackProviderSchema.optional().meta({ title: 'Video source' }),
  gumletAssetId: VideoGumletAssetIdSchema.optional().meta({ title: 'Gumlet asset ID' }),
});
